import { useEffect, useState } from "react";
import axios from "axios";
import BASE_URL from "../config";

export default function PredictionBanner({ symbol }) {
  const [prediction, setPrediction] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setPrediction(null);
    setError(null);
    axios
      .get(`${BASE_URL}/predict/${symbol}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data && res.data.error) setError(res.data.error);
        else setPrediction(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError("Couldn't load the prediction.");
      });
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  if (error) {
    return (
      <section className="panel panel-pad prediction">
        <div className="panel-head">
          <h2 className="kicker">Next day prediction</h2>
          <span className="meta">{symbol}</span>
        </div>
        <div className="state state--error">{error}</div>
      </section>
    );
  }

  if (!prediction) {
    return (
      <section className="panel panel-pad prediction">
        <div className="panel-head">
          <h2 className="kicker">Next day prediction</h2>
          <span className="meta">{symbol}</span>
        </div>
        <div className="prediction-row">
          <div className="skeleton" style={{ width: 220, height: 52 }} />
          <div className="figs" style={{ gap: "2.75rem" }}>
            <div className="skeleton" style={{ width: 90, height: 52 }} />
            <div className="skeleton" style={{ width: 90, height: 52 }} />
          </div>
        </div>
      </section>
    );
  }

  const {
    predicted_return,
    expected_move,
    yesterday_return,
    input_features,
    model_accuracy,
    baseline_accuracy,
    signals,
  } = prediction;
  // NaN/closed-day safe: only treat values as real when finite.
  const predRet = Number.isFinite(predicted_return) ? predicted_return : null;
  const hasPred = predRet !== null;
  const up = hasPred && predRet >= 0;
  const acc = Number.isFinite(model_accuracy) ? Math.round(model_accuracy * 100) : null;
  const base = Number.isFinite(baseline_accuracy) ? Math.round(baseline_accuracy * 100) : null;
  const em = Number.isFinite(expected_move) ? expected_move : null;
  const yest = Number.isFinite(yesterday_return) ? yesterday_return : null;
  const verdictClass = !hasPred ? "muted" : up ? "up-text" : "down-text";

  return (
    <section className="panel panel-pad prediction">
      <div className="panel-head">
        <h2 className="kicker">Next day prediction</h2>
        <span className="meta">{symbol}</span>
      </div>

      <div className="prediction-row">
        <div className="verdict">
          <span className={`arrow ${verdictClass}`}>
            {hasPred ? (up ? "▲" : "▼") : "–"}
          </span>
          <div>
            <div className={`verdict-name ${verdictClass}`}>
              {hasPred ? (up ? "Bullish" : "Bearish") : "No prediction"}
            </div>
            <div className="verdict-sub">
              {hasPred
                ? `Likely to close ${up ? "higher" : "lower"} next session`
                : "Market data incomplete — check back after the next session"}
            </div>
          </div>
        </div>

        <div className="figs">
          <div className="fig">
            <div className="fig-label">Predicted next-day return</div>
            <div className={`fig-value mono ${verdictClass}`}>
              {hasPred ? `${predRet >= 0 ? "+" : ""}${(predRet * 100).toFixed(2)}%` : "—"}
            </div>
          </div>
          {em !== null && (
            <div className="fig">
              <div className="fig-label">Expected move (1σ)</div>
              <div className="fig-value mono">±{(em * 100).toFixed(2)}%</div>
            </div>
          )}
          {acc !== null && (
            <div className="fig">
              <div className="fig-label">Backtested accuracy</div>
              <div className="fig-value mono">{acc}%</div>
              {base !== null && (
                <div className="fig-sub mono">vs {base}% baseline</div>
              )}
            </div>
          )}
        </div>
      </div>

      {signals && signals.length > 0 && (
        <div className="signals">
          {signals.map((s) => (
            <span key={s.label} className={`signal stance-${s.stance}`}>
              {s.label}
              {s.help && (
                <span className="info" title={s.help} aria-label={s.help}>
                  i
                </span>
              )}{" "}
              <b>{s.value}</b>
            </span>
          ))}
        </div>
      )}

      {input_features && (
        <div className="features-line mono">
          Yesterday's change{" "}
          {yest !== null ? (
            <b className={yest >= 0 ? "up-text" : "down-text"}>
              {yest >= 0 ? "+" : ""}
              {(yest * 100).toFixed(2)}%
            </b>
          ) : (
            <b>—</b>
          )}
          <span className="sep">·</span>
          Volatility <b>{input_features.volatility ?? "—"}</b>
          <span className="sep">·</span>
          5-day avg <b>${input_features.ma5 ?? "—"}</b>
        </div>
      )}
    </section>
  );
}
